import React, { useCallback } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { GuestsTourStep } from '../AccommodationsTour';
import type { TabId } from './AccommodationsTabs.types';
type AccommodationsTabsProps = Readonly<{
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
}>;

const AccommodationsTabs: React.FC<AccommodationsTabsProps> = ({ activeTab, onTabChange }) => {
  const { tq } = useTranslation('accommodationsAdmin');

  return (
    <Flex
      role="tablist"
      backgroundColor="white"
      borderBottom="1px solid"
      borderColor="mono3"
      gap={pxToRem(30)}
      alignItems="center"
      paddingTop={4}
      paddingX={{ _: 6, sm2: 8 }}
    >
      <TabItem
        tabId="hotels"
        label={tq`Hotels`}
        isActive={activeTab === 'hotels'}
        onTabChange={onTabChange}
      />
      <TabItem
        tabId="stayAreas"
        label={tq`Neighborhoods`}
        isActive={activeTab === 'stayAreas'}
        onTabChange={onTabChange}
      />
      <GuestsTourStep>
        <Box>
          <TabItem
            tabId="bookings"
            label={tq`Bookings`}
            isActive={activeTab === 'bookings'}
            onTabChange={onTabChange}
          />
        </Box>
      </GuestsTourStep>
    </Flex>
  );
};

type TabItemProps = Readonly<{
  tabId: TabId;
  label: string;
  isActive: boolean;
  onTabChange: (tab: TabId) => void;
}>;

const TabItem: React.FC<TabItemProps> = React.memo(({ tabId, label, isActive, onTabChange }) => {
  const handleClick = useCallback(() => {
    onTabChange(tabId);
  }, [onTabChange, tabId]);

  return (
    <Box
      as="button"
      role="tab"
      aria-selected={isActive}
      onClick={handleClick}
      position="relative"
      cursor="pointer"
      border="none"
      background="none"
      padding={0}
      paddingTop={4}
      paddingBottom={4}
      _focusVisible={{ outline: '2px solid currentColor' }}
    >
      <TextV2
        fontWeight={600}
        fontSize={pxToRem(15)}
        lineHeight={1.3}
        letterSpacing={pxToRem(-0.15)}
        color={isActive ? 'mono14' : 'mono10'}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        textAlign="center"
      >
        {label}
      </TextV2>
      {isActive && (
        <Box
          position="absolute"
          bottom={pxToRem(-1)}
          left={0}
          right={0}
          height={pxToRem(2)}
          backgroundColor="mono14"
          borderRadius={pxToRem(1)}
        />
      )}
    </Box>
  );
});

TabItem.displayName = 'TabItem';

export default AccommodationsTabs;
